import React, { useEffect, useRef, useState } from 'react';
import { Pencil, ArrowRight, Calendar, CalendarClock, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  resolveProject,
  resolvePriority,
  PROJECT_V3,
} from '../../../design-system/taskIdentity';
import { useWorkAreas } from '../../../contexts/WorkAreaContext';
import { EFFORT_OPTIONS } from '../../../lib/taskConstants';
import OrkaStatusChangeButton from './OrkaStatusChangeButton';
import './taskHeader.css';

/**
 * OrkaTaskHeader — V4 task identity strip (May r9 layout).
 *
 *   [grip] [workarea-edge] [TITLE-inline-edit] [📅meeting] [WA▾] [P▾] [E▾] [pencil] [status▾]
 *
 * Title is the focal point. Work-area + priority + effort are inline
 * dropdowns. Meeting is just a calendar glyph. Pencil is intentionally
 * subtle. Status popover lives in the header on the far right (works closed
 * AND open).
 *
 * Props
 *   task                — task model
 *   variant             — 'board' | 'compact' | 'timeline' | 'mobile'
 *   onDragStart/End     — drag passthrough
 *   onMore              — pencil-edit click (subtle)
 *   onReschedule        — calendar action (optional)
 *   onMove              — secondary "move arrow" (optional)
 *   onWorkAreaChange    — (workAreaId, workAreaName) => void
 *   onPriorityChange    — (priorityNumber) => void
 *   onEffortChange      — (effortValue) => void
 *   onTitleChange       — (newTitle) => void  // quick-save inline edit
 *   onStatusChange      — (legacyLabel) => void  // status pill
 *   workAreas           — [{work_area_id, name, color}] override
 *   editableTitle       — boolean (default true). When true, title becomes
 *                         a contentEditable area that quick-saves on
 *                         blur / Enter.
 */
const PRIORITY_OPTIONS = [
  { value: -1, label: 'P-1', long: 'Critical' },
  { value: 0,  label: 'P0',  long: 'Urgent' },
  { value: 1,  label: 'P1',  long: 'High' },
  { value: 2,  label: 'P2',  long: 'Medium' },
  { value: 3,  label: 'P3',  long: 'Standard' },
  { value: 4,  label: 'P4',  long: 'Low' },
  { value: 5,  label: 'P5',  long: 'Backlog' },
];

// Priority colour now flows via PRIORITY_META CSS variables — resolved through
// `resolvePriority(task)` above. No local hex map.

function effortShort(e) {
  if (e.minutes < 60) return `${e.minutes}m`;
  const h = Math.floor(e.minutes / 60);
  const r = e.minutes % 60;
  return r ? `${h}h${r}` : `${h}h`;
}

function toTimeInputValue(v) {
  if (!v) return '';
  // Accept "HH:MM" or "HH:MM:SS"
  const m = String(v).match(/^(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

function TimeRangePill({ start, end, onChange, testId }) {
  const sVal = toTimeInputValue(start);
  const eVal = toTimeInputValue(end);
  const editable = !!onChange;
  if (!editable && !sVal && !eVal) return null;
  return (
    <div className="oth-time-pill" data-testid={testId || 'orka-task-header-time-pill'} onClick={(e) => e.stopPropagation()}>
      <Clock className="w-3 h-3 oth-time-pill-icon" aria-hidden="true" />
      <input
        type="time"
        className="oth-time-input"
        value={sVal}
        disabled={!editable}
        onChange={(e) => { e.stopPropagation(); onChange?.({ task_start_time: e.target.value || null }); }}
        onClick={(e) => e.stopPropagation()}
        aria-label="Start time"
        data-testid={`${testId || 'orka-task-header-time-pill'}-start`}
      />
      <span className="oth-time-arrow" aria-hidden="true">→</span>
      <input
        type="time"
        className="oth-time-input"
        value={eVal}
        disabled={!editable}
        onChange={(e) => { e.stopPropagation(); onChange?.({ task_end_time: e.target.value || null }); }}
        onClick={(e) => e.stopPropagation()}
        aria-label="End time"
        data-testid={`${testId || 'orka-task-header-time-pill'}-end`}
      />
    </div>
  );
}

function InlineTitle({ value, onSave, testId, className }) {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  // Sync DOM when not editing (so external updates flow in).
  useEffect(() => {
    if (!editing && ref.current && ref.current.textContent !== (value || '')) {
      ref.current.textContent = value || '';
    }
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const next = (ref.current?.textContent || '').trim();
    if (next && next !== (value || '')) onSave?.(next);
    else if (ref.current) ref.current.textContent = value || '';
  };

  const cancel = () => {
    setEditing(false);
    if (ref.current) ref.current.textContent = value || '';
    ref.current?.blur();
  };

  return (
    <h3
      ref={ref}
      className={cn('oth-title oth-title--editable', className)}
      contentEditable={!!onSave}
      suppressContentEditableWarning
      spellCheck
      data-testid={testId || 'orka-task-header-title'}
      title={value}
      onFocus={() => setEditing(true)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {value || '(untitled task)'}
    </h3>
  );
}

export function OrkaTaskHeader({
  task = {},
  variant = 'board',
  onDragStart,
  onDragEnd,
  onMore,
  onReschedule,
  onMove,
  onWorkAreaChange,
  onPriorityChange,
  onEffortChange,
  onTitleChange,
  onStatusChange,
  onTimeChange,
  workAreas: workAreasProp,
  editableTitle = true,
  className,
  'data-testid': testId,
  ...rest
}) {
  const project = resolveProject(task);
  const priority = resolvePriority(task);
  const ctx = useWorkAreas();
  const workAreas = workAreasProp || ctx.workAreas || [];

  const compact = variant === 'compact';
  const timeline = variant === 'timeline';
  const mobile = variant === 'mobile';
  const isMeeting = !!(task.task_is_meeting || task.is_meeting);

  const currentWorkAreaId = task.work_area_id
    || workAreas.find(w => w.name === task.work_area_name)?.work_area_id
    || '';

  const workAreaColor = task.project_color_hex
    || ctx.workAreaColors?.[task.work_area_name]
    || ctx.workAreaColors?.[task.work_area_id]
    || project.primary;

  const cssVars = {
    '--workarea-color': workAreaColor,
    '--priority': priority.color,
    '--priority-soft': priority.soft,
    '--priority-text': priority.text,
    '--priority-border': priority.border,
  };

  const currentPriorityValue = task.task_priority ?? task.priority;
  const priorityChipColor = priority.color;
  const priorityChipBorder = priority.border;
  const priorityChipText = priority.text;
  const currentEffort = task.task_estimated_effort ?? 1;

  const titleNode = editableTitle && onTitleChange
    ? <InlineTitle value={task.task_title || task.title} onSave={onTitleChange} />
    : <h3 className="oth-title" data-testid="orka-task-header-title" title={task.task_title || task.title}>
        {task.task_title || task.title || '(untitled task)'}
      </h3>;

  const scheduled = formatScheduled(task);

  return (
    <div
      className={cn('oth', `oth--${variant}`, className)}
      style={cssVars}
      data-testid={testId || 'orka-task-header'}
      data-variant={variant}
      data-state={task.task_status || ''}
      {...rest}
    >
      {/* 1 · DRAG GRIP */}
      {!mobile && (
        <button
          type="button"
          className="oth-grip"
          aria-label={`Drag task ${task.task_title || task.title || ''}`.trim()}
          draggable={!!onDragStart}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onClick={(e) => e.stopPropagation()}
          data-testid="orka-task-header-move"
          title="Drag to move"
        >
          <span className="oth-grip-bars" aria-hidden="true">
            <span /><span /><span /><span /><span /><span />
          </span>
          {!compact && <span className="oth-grip-hint">DRAG</span>}
        </button>
      )}

      {/* 2 · WORK-AREA EDGE */}
      {!mobile && !timeline && (
        <div className="oth-workarea-edge" title={`Work area: ${project.label}`} aria-hidden="true" />
      )}
      {timeline && (
        <div className="oth-time-block" data-testid="orka-task-header-time-block">
          <CalendarClock className="w-3.5 h-3.5 opacity-80" aria-hidden="true" />
          <div>
            <strong>{scheduled || '—'}</strong>
          </div>
        </div>
      )}

      {/* 3 · MAIN — title-led */}
      <div className="oth-main oth-main--title-led">
        {/* Row A — title is the focal point (largest type) */}
        <div className="oth-title-row">
          {titleNode}
        </div>

        {/* Row B — controls cluster.
            Left cluster:  workarea select · time pill (start → finish)
            Right cluster: meeting glyph · priority · effort · pencil · status.
            Clusters are visually separated by `margin-left: auto` and a soft divider on hover. */}
        <div className="oth-controls-row">
          <div className="oth-cluster oth-cluster--identity" data-testid="orka-task-header-cluster-identity">
            <div className="oth-workarea-select-wrap" data-testid="orka-task-header-workarea-select-wrap">
              <span
                className="oth-workarea-flash"
                aria-hidden="true"
                style={{ background: workAreaColor }}
              />
              <select
                className="oth-workarea-select"
                value={currentWorkAreaId}
                onChange={(e) => {
                  e.stopPropagation();
                  const id = e.target.value;
                  const wa = workAreas.find(w => w.work_area_id === id);
                  onWorkAreaChange?.(id, wa?.name || '');
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={!onWorkAreaChange}
                aria-label="Change work area"
                data-testid="orka-task-header-workarea-select"
              >
                <option value="">{project.label || 'Untagged'}</option>
                {workAreas.map(w => (
                  <option key={w.work_area_id} value={w.work_area_id}>{w.name}</option>
                ))}
              </select>
            </div>

            <TimeRangePill
              start={task.task_local_start_time}
              end={task.task_local_end_time}
              onChange={onTimeChange}
            />
          </div>

          <div className="oth-cluster oth-cluster--signals" data-testid="orka-task-header-cluster-signals">
            {isMeeting && (
              <span className="oth-meeting-icon" data-testid="orka-task-header-meeting" title="Meeting" aria-label="Meeting">
                <Calendar className="w-3.5 h-3.5" />
              </span>
            )}

            <div className="oth-priority-select-wrap" style={{ borderColor: priorityChipBorder }} data-testid="orka-task-header-priority-wrap">
              <span className="oth-priority-dot" style={{ background: priorityChipColor }} />
              <select
                className="oth-priority-select"
                style={{ color: priorityChipText }}
                value={currentPriorityValue ?? 3}
                onChange={(e) => { e.stopPropagation(); onPriorityChange?.(Number(e.target.value)); }}
                onClick={(e) => e.stopPropagation()}
                disabled={!onPriorityChange}
                aria-label="Change priority"
                data-testid="orka-task-header-priority-select"
              >
                {PRIORITY_OPTIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label} · {p.long}</option>
                ))}
              </select>
            </div>

            <div className="oth-effort-select-wrap" data-testid="orka-task-header-effort-wrap">
              <select
                className="oth-effort-select"
                value={currentEffort}
                onChange={(e) => { e.stopPropagation(); onEffortChange?.(Number(e.target.value)); }}
                onClick={(e) => e.stopPropagation()}
                disabled={!onEffortChange}
                aria-label="Change effort"
                data-testid="orka-task-header-effort-select"
              >
                {EFFORT_OPTIONS.map(e => (
                  <option key={e.value} value={e.value}>E{e.value} · {effortShort(e)}</option>
                ))}
              </select>
            </div>

            <span className="oth-cluster-divider" aria-hidden="true" />

            {onMove && (
              <button type="button" className="oth-icon-btn" onClick={(e) => { e.stopPropagation(); onMove(e); }} aria-label="Move">
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {onReschedule && (
              <button type="button" className="oth-icon-btn" onClick={(e) => { e.stopPropagation(); onReschedule(e); }} aria-label="Schedule">
                <Calendar className="w-4 h-4" />
              </button>
            )}
            {onMore && (
              <button type="button" className="oth-icon-btn oth-edit-btn--subtle" onClick={(e) => { e.stopPropagation(); onMore(e); }} aria-label="Edit task" data-testid="orka-task-header-more">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onStatusChange && (
              <OrkaStatusChangeButton
                state={task.task_status}
                onChange={onStatusChange}
                size="sm"
                surface="on-gradient"
                showChangeHint
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatScheduled(task) {
  if (task.scheduled) return task.scheduled;
  if (task.task_local_start_time && task.task_local_end_time) return `${task.task_local_start_time}–${task.task_local_end_time}`;
  if (task.task_local_start_time) return task.task_local_start_time;
  if (task.task_scheduled_date) return task.task_scheduled_date;
  return null;
}

export { PROJECT_V3 as ORKA_TASK_PROJECTS };
export default OrkaTaskHeader;
